import { useRouter } from 'next/router';
import { useCallback } from 'react';

import type { TradeDirection } from '@/components/TransactionModule/types';
import { isSameQuery } from '@/components/TransactionModule/utils';

import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';
import { useInitializeDefaultTokens } from '@/hooks/useInitializeDefaultTokens';
import { useSelectedTokens } from '@/hooks/useSelectedTokens';

import { TokenBase } from '@/utils/0x/token-registry.types';
import { ChainAddress } from '@/utils/models';
import { resolveDefaultTokenPair } from '@/utils/swap';
import type { TokenInfo } from '@/utils/token.types';

/** Pages whose query string carries the trade module's pair. */
type TokenPairPathname = '/' | '/trade' | '/swap';

/**
 * URL-driven token pair for a page hosting the trade module: the pair lives
 * in the page's query string, user actions push history entries, and empty
 * fields fill with the default pair.
 */
export const useUrlTokenPair = (pathname: TokenPairPathname) => {
  const router = useRouter();
  const { push, replace, query } = router;

  const selectedTokens = useSelectedTokens();
  // Read prefetched defaults on the first render instead of waiting for the URL effect.
  const { buyTokenAddress, buyTokenChainId, sellTokenAddress, sellTokenChainId } = router.isReady
    ? resolveDefaultTokenPair(selectedTokens)
    : selectedTokens;

  const { token: sellToken } = useTokenResult(sellTokenAddress, sellTokenChainId);
  const { token: buyToken } = useTokenResult(buyTokenAddress, buyTokenChainId);

  const buildTokenQuery = useCallback(
    (
      newBuyAddress?: ChainAddress,
      newBuyChain?: number,
      newSellAddress?: ChainAddress,
      newSellChain?: number,
    ) => {
      const { buyAddress, buyChain, sellAddress, sellChain, ...otherParams } = query;
      const newQuery = { ...otherParams };

      if (newBuyAddress) newQuery.buyAddress = newBuyAddress;
      if (newBuyChain) newQuery.buyChain = newBuyChain.toString();
      if (newSellAddress) newQuery.sellAddress = newSellAddress;
      if (newSellChain) newQuery.sellChain = newSellChain.toString();

      return newQuery;
    },
    [query],
  );

  // User actions (select/flip) push so Back undoes them one at a time.
  const syncUrlParameters = useCallback(
    (
      newBuyAddress?: ChainAddress,
      newBuyChain?: number,
      newSellAddress?: ChainAddress,
      newSellChain?: number,
    ) => {
      const newQuery = buildTokenQuery(newBuyAddress, newBuyChain, newSellAddress, newSellChain);
      if (isSameQuery(newQuery, query)) return;
      push({ pathname, query: newQuery }, undefined, { shallow: true });
    },
    [buildTokenQuery, pathname, push, query],
  );

  // Hook-initiated fills replace so the automatic default pair never adds a
  // history entry - Back must leave the page, not strip the params and
  // re-trigger the fill (this also keeps the widget iframe from polluting the
  // embedder's session history on load).
  const replaceUrlParameters = useCallback(
    (
      newBuyAddress?: ChainAddress,
      newBuyChain?: number,
      newSellAddress?: ChainAddress,
      newSellChain?: number,
    ) => {
      const newQuery = buildTokenQuery(newBuyAddress, newBuyChain, newSellAddress, newSellChain);
      if (isSameQuery(newQuery, query)) return;
      replace({ pathname, query: newQuery }, undefined, { shallow: true });
    },
    [buildTokenQuery, pathname, replace, query],
  );

  // Fill any empty token field with the static default pair.
  useInitializeDefaultTokens({
    ...selectedTokens,
    syncUrlParameters: replaceUrlParameters,
  });

  const handleFlipSelectedTokens = useCallback(() => {
    syncUrlParameters(sellTokenAddress, sellTokenChainId, buyTokenAddress, buyTokenChainId);
  }, [buyTokenAddress, buyTokenChainId, sellTokenAddress, sellTokenChainId, syncUrlParameters]);

  const handleSelectToken = useCallback(
    (selectedToken: Pick<TokenBase, 'address' | 'chainId'>, side: TradeDirection) => {
      const { address, chainId } = selectedToken;

      if (side === 'sell') {
        setTimeout(() => document.getElementById('sellAmount')?.focus(), 0);
      }

      if (side === 'buy') {
        syncUrlParameters(address, chainId, sellTokenAddress, sellTokenChainId);
      } else {
        syncUrlParameters(buyTokenAddress, buyTokenChainId, address, chainId);
      }
    },
    [buyTokenAddress, buyTokenChainId, syncUrlParameters, sellTokenAddress, sellTokenChainId],
  );

  const handleSelectPair = useCallback(
    (nextBuyToken: TokenInfo, nextSellToken: TokenInfo) => {
      syncUrlParameters(
        nextBuyToken.address,
        nextBuyToken.chainId,
        nextSellToken.address,
        nextSellToken.chainId,
      );
    },
    [syncUrlParameters],
  );

  return {
    buyToken,
    sellToken,
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    handleFlipSelectedTokens,
    handleSelectToken,
    handleSelectPair,
  };
};
